import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { supabase } from '../../lib/supabase';
import { createTransactionWithEntries } from '../../lib/transactions';
import { AlertCircle, Loader2, Minus, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';

const CheckoutPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState<any>(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [entryId, setEntryId] = useState<string | null>(null);
  
  // Calculate total cost
  const totalCost = campaign ? campaign.ticket_price * ticketQuantity : 0;

  useEffect(() => {
    if (!campaignId) {
      setError('Invalid campaign ID');
      setLoading(false);
      return;
    }
    fetchCampaignDetails();
  }, [campaignId]);

  const fetchCampaignDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Campaign not found');
      
      setCampaign(data);
      setLoading(false);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to load campaign');
      setLoading(false);
    }
  };

  const handleUserInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (value: number) => {
    if (value >= 1) {
      setTicketQuantity(value);
    }
  };

  const createEntry = async () => {
    try {
      // Validate user info
      if (!userInfo.name || !userInfo.email) {
        throw new Error('Please provide your name and email');
      }

      // Create entry record
      const { data, error } = await supabase
        .from('entries')
        .insert({
          campaign_id: campaignId,
          name: userInfo.name,
          email: userInfo.email,
          phone_number: userInfo.phone,
          payment_method: 'online',
          payment_status: 'pending',
          quantity: ticketQuantity,
          total_amount: totalCost
        })
        .select()
        .single();

      if (error) throw error;
      
      setEntryId(data.id);
      return data.id;
    } catch (error: any) {
      console.error('Error creating entry:', error);
      throw error;
    }
  };

  const handlePayPalApprove = async (data: any) => {
    try {
      setLoading(true);
      
      // If no entry created yet, create one
      const currentEntryId = entryId || await createEntry();
      
      // Capture the order
      const response = await fetch(`/api/paypal/capture-order/${data.orderID}`, {
        method: 'POST',
      });

      const responseData = await response.json();
      if (responseData.error) {
        throw new Error(responseData.error);
      }

      // Record transaction and create tickets
      await createTransactionWithEntries({
        campaignId: campaignId!,
        entryId: currentEntryId,
        amount: totalCost,
        paymentMethod: 'paypal',
        status: 'completed',
        paypalPaymentId: data.orderID,
        numberOfTickets: ticketQuantity,
      });

      // Show success message
      toast.success('Payment successful! Your tickets have been created.');
      
      // Redirect to success page
      navigate(`/checkout/success/${currentEntryId}`);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to complete payment');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
        <p>Loading checkout...</p>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-center text-red-600 mb-4">
          <AlertCircle className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Checkout Error</h2>
        <p className="text-gray-600 text-center mb-6">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
        >
          Return Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Checkout</h2>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Campaign:</span>
          <span className="font-semibold">{campaign.name}</span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Ticket Price:</span>
          <span className="font-semibold">${campaign.ticket_price.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-600">Quantity:</span>
          <div className="flex items-center">
            <button 
              onClick={() => handleQuantityChange(ticketQuantity - 1)}
              className="bg-gray-200 px-2 py-1 rounded-l-md"
              disabled={ticketQuantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="px-4 py-1 bg-white border-t border-b">{ticketQuantity}</span>
            <button 
              onClick={() => handleQuantityChange(ticketQuantity + 1)}
              className="bg-gray-200 px-2 py-1 rounded-r-md"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex justify-between items-center font-bold">
          <span>Total:</span>
          <span>${totalCost.toFixed(2)}</span>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">Your Information</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={userInfo.name}
              onChange={handleUserInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={userInfo.email}
              onChange={handleUserInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
            <input
              type="tel"
              name="phone"
              value={userInfo.phone}
              onChange={handleUserInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">Payment Method</h3>
        <PayPalButtons
          style={{ layout: "vertical" }}
          createOrder={(data, actions) => {
            if (!actions.order) return Promise.reject('PayPal actions not available');
            return actions.order.create({
              intent: "CAPTURE",
              purchase_units: [
                {
                  amount: {
                    currency_code: "USD",
                    value: totalCost.toFixed(2),
                  },
                  description: `${ticketQuantity} ticket(s) for ${campaign.name}`
                },
              ],
            });
          }}
          onApprove={(data, actions) => {
            return handlePayPalApprove(data);
          }}
          onError={(err) => {
            console.error('PayPal Error:', err);
            setError('Payment failed. Please try again.');
          }}
        />
      </div>

      <div className="text-sm text-gray-500 text-center mt-6">
        Your payment is secure and encrypted
      </div>
    </div>
  );
};

export default CheckoutPage;
